/**
 * Uslovi korišćenja po klijentu - ko je prihvatio, kad, i dugme da se traži ponovo.
 *
 * „Resetuj" ovde **ne briše ništa**. Upisuje se datum poništavanja, i od tog
 * trenutka ranije prihvatanje važi kao starije od njega; korisnik pri sledećem
 * pozivu dobija ekran sa uslovima, a kad prihvati, u istoriji stoje **oba**
 * datuma. Zato ova kartica i prikazuje celu istoriju, a ne samo poslednji red:
 * inače se ne bi videlo da prošlost nije nestala (ADR-042).
 */
import { Fragment, useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Check, Clock, RefreshCw, RotateCcw, X } from 'lucide-react';
import { api } from '../../../api/client';
import { useEscapeToClose } from '../../../hooks/useEscapeToClose';

type Kind = 'terms' | 'privacy';

type DocStatus = {
  kind: Kind;
  version: number;
  acceptedAt: string | null;
  pending: boolean;
};

type Acceptance = {
  id: string;
  /** Istorija sadrži i prihvatanje uslova partnerskog programa (ADR-071) - ono ne ulazi u gejt prijave. */
  kind: Kind | 'affiliate_terms';
  version: number;
  acceptedAt: string;
  afterReset: boolean;
  ip: string;
};

type UserRow = {
  userId: string;
  username: string;
  fullName: string;
  role: string;
  isActive: boolean;
  status: DocStatus[];
  history: Acceptance[];
};

type ResetRow = {
  id: string;
  userId: string | null;
  kind: Kind | null;
  resetAt: string;
  reason: string;
  resetByEmail: string;
};

type Odgovor = {
  documents: Array<{ kind: Kind; version: number; title: string; requiresAcceptance: boolean; effectiveFrom: string }>;
  users: UserRow[];
  resets: ResetRow[];
};

const NAZIV: Record<Kind | 'affiliate_terms', string> = {
  terms: 'Uslovi korišćenja',
  privacy: 'Politika privatnosti',
  affiliate_terms: 'Uslovi partnerskog programa',
};

function trenutak(iso: string | null): string {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  return `${d.toLocaleDateString('sr-RS')} ${d.toLocaleTimeString('sr-RS', { hour: '2-digit', minute: '2-digit' })}`;
}

export function LegalTab({ tenantId }: { tenantId: string }) {
  const [data, setData] = useState<Odgovor | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [otvoren, setOtvoren] = useState<string | null>(null);
  const [dijalog, setDijalog] = useState<{ user: UserRow | null } | null>(null);
  const [razlog, setRazlog] = useState('');
  const [vrsta, setVrsta] = useState<'' | Kind>('');
  const [salje, setSalje] = useState(false);
  useEscapeToClose(() => { if (!salje) setDijalog(null); }, !!dijalog);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await api.get<Odgovor>(`/admin/tenants/${tenantId}/legal`));
    } catch (e: any) {
      setError(e?.message || 'Greška pri učitavanju');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { void refresh(); }, [refresh]);

  const posalji = async () => {
    if (!dijalog) return;
    setSalje(true);
    try {
      await api.post(`/admin/tenants/${tenantId}/legal/reset`, {
        userId: dijalog.user?.userId,
        kind: vrsta || undefined,
        reason: razlog.trim(),
      });
      setDijalog(null);
      setRazlog('');
      setVrsta('');
      await refresh();
    } catch (e: any) {
      setError(e?.message || 'Poništavanje nije upisano');
    } finally {
      setSalje(false);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: 'var(--color-text-strong)' }}>
          Prihvatanje uslova
        </h3>
        <button onClick={() => void refresh()} style={btnGhost}>
          <RefreshCw size={13} /> Osveži
        </button>
        <button
          onClick={() => { setDijalog({ user: null }); setRazlog(''); setVrsta(''); }}
          style={{ ...btnWarn, marginLeft: 'auto' }}
        >
          <RotateCcw size={13} /> Traži ponovno prihvatanje - svi korisnici
        </button>
      </div>

      {data && (
        <p style={{ margin: '0 0 14px', fontSize: 12, color: 'var(--color-text-muted)' }}>
          Važeće verzije:{' '}
          {data.documents
            .filter((d) => d.requiresAcceptance)
            .map((d) => `${NAZIV[d.kind] ?? d.title} v${d.version}`)
            .join(' · ') || 'nijedan dokument nije objavljen'}
        </p>
      )}

      {error && (
        <div style={upozorenje}>
          <AlertTriangle size={14} /> {error}
        </div>
      )}
      {loading && <p style={{ color: 'var(--color-text-muted)', fontSize: 13 }}>Učitavanje…</p>}

      {data && data.users.length === 0 && (
        <p style={{ color: 'var(--color-text-muted)', fontSize: 13 }}>Klijent nema nijednog korisnika.</p>
      )}

      {data && data.users.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr>
              {['Korisnik', 'Uslovi korišćenja', 'Politika privatnosti', ''].map((h) => (
                <th key={h} style={th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.users.map((u) => {
              const razvijen = otvoren === u.userId;
              return (
                <Fragment key={u.userId}>
                  <tr>
                    <td style={td}>
                      <div style={{ fontWeight: 600, color: 'var(--color-text-strong)' }}>{u.fullName || u.username}</div>
                      <div style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
                        {u.username} · {u.role}{u.isActive ? '' : ' · neaktivan'}
                      </div>
                    </td>
                    {(['terms', 'privacy'] as Kind[]).map((k) => {
                      const st = u.status.find((s) => s.kind === k);
                      if (!st) return <td key={k} style={td}><span style={{ color: 'var(--color-text-muted)' }}>-</span></td>;
                      return (
                        <td key={k} style={td}>
                          {st.pending ? (
                            <span style={{ ...znacka, background: 'rgba(217,119,6,0.12)', color: '#b45309' }}>
                              <Clock size={12} /> čeka prihvatanje
                            </span>
                          ) : (
                            <span style={{ ...znacka, background: 'rgba(22,163,74,0.12)', color: '#15803d' }}>
                              <Check size={12} /> {trenutak(st.acceptedAt)}
                            </span>
                          )}
                        </td>
                      );
                    })}
                    <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button onClick={() => setOtvoren(razvijen ? null : u.userId)} style={btnGhost}>
                        {razvijen ? 'Sakrij' : `Istorija (${u.history.length})`}
                      </button>
                      <button
                        onClick={() => { setDijalog({ user: u }); setRazlog(''); setVrsta(''); }}
                        style={{ ...btnWarn, marginLeft: 6 }}
                      >
                        <RotateCcw size={13} /> Traži ponovo
                      </button>
                    </td>
                  </tr>

                  {razvijen && (
                    <tr>
                      <td colSpan={4} style={{ ...td, background: 'var(--color-bg)' }}>
                        {u.history.length === 0 ? (
                          <span style={{ color: 'var(--color-text-muted)' }}>Nema nijednog zabeleženog prihvatanja.</span>
                        ) : (
                          <ol style={{ margin: 0, paddingLeft: 18, display: 'grid', gap: 4 }}>
                            {u.history.map((h) => (
                              <li key={h.id}>
                                <strong>{trenutak(h.acceptedAt)}</strong>{' - '}
                                {NAZIV[h.kind]} v{h.version}
                                {h.afterReset ? ' · posle poništavanja' : ' · prvo prihvatanje'}
                                {h.ip ? ` · ${h.ip}` : ''}
                              </li>
                            ))}
                          </ol>
                        )}
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      )}

      {data && data.resets.length > 0 && (
        <section style={{ marginTop: 24 }}>
          <h4 style={{ margin: '0 0 8px', fontSize: 13, fontWeight: 600, color: 'var(--color-text-strong)' }}>
            Zatrazena ponovna prihvatanja
          </h4>
          <ol style={{ margin: 0, paddingLeft: 18, display: 'grid', gap: 4, fontSize: 12, color: 'var(--color-text-muted)' }}>
            {data.resets.map((r) => (
              <li key={r.id}>
                {trenutak(r.resetAt)} - {r.kind ? NAZIV[r.kind] : 'svi dokumenti'}
                {r.userId ? ' · jedan korisnik' : ' · svi korisnici'}
                {r.resetByEmail ? ` · ${r.resetByEmail}` : ''}
                {r.reason ? ` · „${r.reason}"` : ''}
              </li>
            ))}
          </ol>
        </section>
      )}

      {dijalog && (
        <div style={overlay}>
          <div style={modal} onClick={(e) => e.stopPropagation()}>
            <header style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <RotateCcw size={16} />
              <strong style={{ color: 'var(--color-text-strong)' }}>Traži ponovno prihvatanje</strong>
              <button onClick={() => setDijalog(null)} style={{ ...btnGhost, marginLeft: 'auto' }}><X size={14} /></button>
            </header>

            <p style={{ margin: '0 0 12px', fontSize: 13, color: 'var(--color-text-muted)' }}>
              {dijalog.user
                ? `Korisnik ${dijalog.user.fullName || dijalog.user.username} će pri sledećem pozivu dobiti ekran sa uslovima.`
                : 'Svi korisnici ovog klijenta će pri sledećem pozivu dobiti ekran sa uslovima.'}
              {' '}Ranija prihvatanja se <strong>ne brišu</strong> - ostaju u istoriji sa svojim datumom.
            </p>

            <label style={oznaka}>Dokument</label>
            <select value={vrsta} onChange={(e) => setVrsta(e.target.value as '' | Kind)} style={polje}>
              <option value="">Oba dokumenta</option>
              <option value="terms">Samo uslovi korišćenja</option>
              <option value="privacy">Samo politika privatnosti</option>
            </select>

            <label style={oznaka}>Razlog (ostaje zabeležen)</label>
            <input
              value={razlog}
              onChange={(e) => setRazlog(e.target.value)}
              placeholder="npr. izmena člana o naplati"
              style={polje}
            />

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 16, width: '100%', maxWidth: 360, marginLeft: 'auto', marginRight: 'auto' }}>
              <button type="button" onClick={() => setDijalog(null)} style={{ ...btnGhost, width: '100%', textAlign: 'center' }} disabled={salje}>Otkaži</button>
              <button onClick={() => void posalji()} style={{ ...btnWarn, width: '100%', justifyContent: 'center' }} disabled={salje}>
                {salje ? 'Upisujem…' : 'Zatraži ponovno prihvatanje'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const th: React.CSSProperties = {
  textAlign: 'left', padding: '8px 10px', fontSize: 11, textTransform: 'uppercase',
  letterSpacing: '0.04em', color: 'var(--color-text-muted)',
  borderBottom: '1px solid var(--color-border)',
};

const td: React.CSSProperties = {
  padding: '10px', borderBottom: '1px solid var(--color-border)', verticalAlign: 'top',
};

const znacka: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 5,
  padding: '3px 8px', borderRadius: 999, fontSize: 12, whiteSpace: 'nowrap',
};

const btnGhost: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 5,
  padding: '5px 10px', borderRadius: 6, fontSize: 12, cursor: 'pointer',
  border: '1px solid var(--color-border)', background: 'transparent',
  color: 'var(--color-text-muted)',
};

const btnWarn: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 5,
  padding: '5px 10px', borderRadius: 6, fontSize: 12, cursor: 'pointer',
  border: '1px solid #f59e0b', background: 'rgba(245,158,11,0.10)', color: '#b45309',
};

const upozorenje: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8,
  padding: '8px 12px', borderRadius: 6, marginBottom: 12,
  background: 'rgba(220,38,38,0.08)', color: '#dc2626', fontSize: 13,
};

const overlay: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 60, padding: 20,
};

const modal: React.CSSProperties = {
  width: '100%', maxWidth: 480, borderRadius: 10, padding: 20,
  background: 'var(--color-bg-surface, #fff)', border: '1px solid var(--color-border)',
};

const oznaka: React.CSSProperties = {
  display: 'block', fontSize: 12, fontWeight: 600, margin: '10px 0 4px',
  color: 'var(--color-text-strong)',
};

const polje: React.CSSProperties = {
  width: '100%', padding: '8px 10px', borderRadius: 6, fontSize: 13,
  border: '1px solid var(--color-border)', background: 'var(--color-bg-surface, #fff)',
  color: 'var(--color-text-strong)',
};
